"use client";

import type { SupportedLanguage } from "@/shared/language";
import {
 DEFAULT_LANGUAGE,
 LANGUAGE_COOKIE_KEY,
 LANGUAGE_COOKIE_MAX_AGE_SECONDS,
 LANGUAGE_STORAGE_KEY,
 isSupportedLanguage,
} from "@/shared/language";
import {
 ReactNode,
 createContext,
 useContext,
 useEffect,
 useMemo,
 useState,
} from "react";

interface LanguageContextValue {
 language: SupportedLanguage;
 setLanguage: (value: SupportedLanguage) => void;
 direction: "ltr" | "rtl";
}

const LanguageContext = createContext<LanguageContextValue | undefined>(
 undefined
);

export function LanguageProvider({ children }: { children: ReactNode }) {
 const [language, setLanguage] = useState<SupportedLanguage>(DEFAULT_LANGUAGE);

 const direction = useMemo(
  () => (language === "ar" ? "rtl" : "ltr"),
  [language]
 );

 useEffect(() => {
  if (typeof window === "undefined") {
   return;
  }

  const cookieLanguage = document.cookie
   .split("; ")
   .find((entry) => entry.startsWith(`${LANGUAGE_COOKIE_KEY}=`))
   ?.split("=")[1];

  if (isSupportedLanguage(cookieLanguage)) {
   setLanguage(cookieLanguage);
   return;
  }

  const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  if (isSupportedLanguage(stored)) {
   setLanguage(stored);
  }
 }, []);

 useEffect(() => {
  if (typeof document === "undefined") {
   return;
  }
  document.documentElement.setAttribute("lang", language);
  document.documentElement.setAttribute("dir", direction);

  if (typeof window !== "undefined") {
   window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  }

  if (typeof document !== "undefined") {
   document.cookie = `${LANGUAGE_COOKIE_KEY}=${language}; path=/; max-age=${LANGUAGE_COOKIE_MAX_AGE_SECONDS}; SameSite=Lax`;
  }
 }, [language, direction]);

 const value = useMemo(
  () => ({
   language,
   setLanguage,
   direction,
  }),
  [language, direction]
 );

 return (
  <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
 );
}

export function useLanguage() {
 const context = useContext(LanguageContext);
 if (!context) {
  throw new Error("useLanguage must be used within a LanguageProvider");
 }
 return context;
}

export type { SupportedLanguage };
